import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./JobProgressModal.css";

const FUN_MESSAGE_KEYS = [
  "funMessagesModal.goForSwim",
  "funMessagesModal.perfectTimeForCoffee",
  "funMessagesModal.quickStretch",
  "funMessagesModal.hydrationCheck",
  "funMessagesModal.takeWalk",
  "funMessagesModal.callSomeone",
  "funMessagesModal.petYourPet",
  "funMessagesModal.touchGrass",
  "funMessagesModal.jumpingJacks",
  "funMessagesModal.victoryDance",
  "funMessagesModal.deepBreaths",
  "funMessagesModal.timeToSnack",
];

/** Format remaining minutes into a human-readable string */
function formatTimeRemaining(minutes, t) {
  if (minutes <= 0) return t('almostDone');
  if (minutes < 2) return t('underTwoMinutes');
  if (minutes < 60) return t('minutesRemaining', { count: Math.ceil(minutes) });
  const hours = Math.floor(minutes / 60);
  const mins = Math.ceil(minutes % 60);
  return t('hoursMinutesRemaining', { hours, minutes: mins });
}

/** Get human-readable phase label based on progress percentage */
function getPhaseLabel(progress, videoDuration, t) {
  if (progress >= 100) return t('phaseDone');
  if (progress >= 56) return t('phaseRendering');
  if (progress >= 55) return t('phaseAnalyzing');
  if (progress >= 40) {
    const label = t('phaseTranscribing');
    return videoDuration > 3600 ? `${label} ${t('phaseTranscribingLong')}` : label;
  }
  if (progress >= 25) return t('phaseExtractingAudio');
  if (progress >= 15) return t('phaseDownloaded');
  return t('phaseDownloading');
}

const JobProgressModal = ({ job, videoTitle, onClose }) => {
  const { t } = useTranslation('clips');
  const [now, setNow] = useState(Date.now());

  const _id = job?._id;
  const progress = job?.progress || 0;
  const status = job?.status;
  const createdAt = job?.createdAt;
  const videoDuration = job?.videoDuration;
  // Per-stage ETA from worker (seconds). Worker updates this on each
  // progress webhook — way more accurate than our client-side guess.
  const etaSeconds = job?.etaSeconds;
  const isProcessing = status === "queued" || status === "processing";

  // Tick every second so elapsed/remaining updates live
  useEffect(() => {
    if (!isProcessing) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isProcessing]);

  if (!job) return null;

  // Compute dynamic time estimate.
  // Preferred source: worker-provided etaSeconds (per-stage heuristic, more
  // accurate). Fallback: rough client-side estimate from elapsed + duration.
  const elapsedMin = (now - new Date(createdAt).getTime()) / 60000;
  let remainingMin = null;
  if (typeof etaSeconds === "number" && etaSeconds >= 0) {
    remainingMin = etaSeconds / 60;
  } else if (videoDuration) {
    const estimatedTotalMin = 14 + Math.ceil(videoDuration / 600);
    remainingMin = estimatedTotalMin - elapsedMin;
  }

  const timeDisplay = !isProcessing
    ? null
    : remainingMin === null
      ? t('estimatingTime')
      : formatTimeRemaining(remainingMin, t);

  const phaseLabel = getPhaseLabel(progress, videoDuration || 0, t);

  // Get consistent fun message for this job
  const getFunMessage = () => {
    const index = _id.charCodeAt(_id.length - 1) % FUN_MESSAGE_KEYS.length;
    return t(FUN_MESSAGE_KEYS[index]);
  };

  return (
    <div className="job-modal-overlay" onClick={onClose}>
      <div className="job-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="job-modal-close" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Header */}
        <div className="job-modal-header">
          <div className="job-modal-icon">
            <div className="job-modal-spinner" />
          </div>
          <h3 className="job-modal-title">{t('creatingYourClips')}</h3>
          {videoTitle && (
            <p className="job-modal-subtitle">{videoTitle}</p>
          )}
        </div>

        {/* Progress section */}
        <div className="job-modal-progress-section">
          {/* Progress bar */}
          <div className="job-modal-progress-bar">
            <div
              className="job-modal-progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Progress info */}
          <div className="job-modal-progress-info">
            <span className="job-modal-percentage">{Math.round(progress)}%</span>
            {timeDisplay && (
              <span className="job-modal-time-remaining">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                {timeDisplay}
              </span>
            )}
          </div>
        </div>

        {/* Current phase */}
        {isProcessing && (
          <div className="job-modal-step">
            <span className="job-modal-step-dot" />
            {phaseLabel}
          </div>
        )}

        {/* Fun message */}
        <div className="job-modal-fun-message">
          {getFunMessage()}
        </div>

        {/* Email notification note */}
        <div className="job-modal-email-note">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <polyline points="22 4 12 13 2 4" />
          </svg>
          {t('wellEmailYouWhenReady')}
        </div>
      </div>
    </div>
  );
};

export default JobProgressModal;
